(ns arthur.ui.stage
  "The stage: the raster canvas, and the SVG editor over it.

  They are one widget and live in one namespace. The canvas is the playback sink
  — `ui/player`'s loop writes it from an animation frame, so it has no reactive
  content and scrubbing at speed does not re-render this component — and the SVG
  is the only thing on the page that draws a shape a human can grab. Splitting
  them would mean two namespaces sharing one coordinate transform.

  THE CANVAS IS THE RASTER'S OWN SIZE, scaled by CSS. See `ui/canvas` for why
  that is load-bearing rather than convenient."
  (:require [arthur.domain.channel :as channel]
            [arthur.domain.clip :as clip]
            [arthur.domain.gesture :as gesture]
            [arthur.domain.nest :as nest]
            [arthur.domain.node :as node]
            [arthur.domain.outline :as outline]
            [arthur.domain.paint :as paint]
            [arthur.domain.pick :as pick]
            [arthur.domain.symbol :as symbol]
            [arthur.events.paint :as paint-events]
            [arthur.events.ui :as ui]
            [arthur.footage.store :as store]
            [arthur.subs.playback :as playback]
            [arthur.subs.render :as render]
            [arthur.subs.ui :as sub]
            [arthur.ui.drag :as drag]
            [arthur.ui.layout :as layout]
            [arthur.ui.player :as player]
            [arthur.ui.tracing :as tracing]
            [arthur.ui.shortcuts :as shortcuts]
            [arthur.ui.tools :as tools]
            [arthur.ui.timeline :as timeline]
            [re-frame.core :as rf]
            [reagent.core :as r]))

;; Pointer coordinates come from the SVG transform, in stage pixels.

(defn- event-point [element event]
  (let [svg (if (= "svg" (.-tagName element)) element (.querySelector element "svg.paint-overlay"))
        pt (.createSVGPoint svg)]
    (set! (.-x pt) (.-clientX event))
    (set! (.-y pt) (.-clientY event))
    (let [p (.matrixTransform pt (.inverse (.getScreenCTM svg)))] [(.-x p) (.-y p)])))

(defn stage-point [event _w _h]
  (mapv js/Math.round (event-point (.-currentTarget event) event)))

(defn- pairs [pts] (mapv vec (partition 2 pts)))

(defn- points-text [pts]
  (apply str (interpose " " (map (fn [[x y]] (str x "," y)) (pairs pts)))))

;; Which vertex the pointer has captured. A PLAIN atom, not app-db and not a
;; ratom: it is pointer bookkeeping that lives for the length of one drag, it is
;; written on every pointermove, and nothing renders from it — the moves it
;; produces go straight out as `::set-vertex`, which is where the document
;; changes and where re-frame belongs.
(defonce ^:private dragging (atom nil))
(defonce ^:private selection-anchor (atom nil))

(defn- editing
  "The selected node when it is a polygon on screen, however deep it is nested,
  as `[sid id geom active-key editable? frame matrix]`: its own frame, and the
  matrix from its coordinates to the stage's. See `::sub/selected-local`."
  []
  (let [[sid id n] @(rf/subscribe [::sub/selected-node])
        {:keys [frame matrix]} @(rf/subscribe [::sub/selected-local])]
    (when (and (:paint? n) matrix)
      (let [geom   (get-in n [:channels paint/geometry])
            active (when geom (paint/active-frame geom frame))]
        [sid id geom active
         ;; A frame between two drawing keys with a tween running has no vertices
         ;; of its own to move: what is on screen there is interpolated, and
         ;; dragging it would silently edit the key behind it instead.
         (or (not= :linear (channel/segment-interp geom active))
             (contains? (:keys geom) frame))
         frame matrix]))))

(defn- through
  "Flat points `pts` through matrix `m`."
  [m pts]
  (let [out (js/Float64Array. 2)]
    (into [] (mapcat (fn [[x y]] (node/apply-pt! out 0 m x y) [(aget out 0) (aget out 1)]))
          (partition 2 pts))))

(defn- ghost
  "Where a drag out of the pool would land: the outline of its first frame,
  dashed, and a cross on the middle it will land on, which goes under the
  pointer. Drawn from the drag's own outline rather than by resolving anything,
  so hovering costs one re-render and no evaluation. The cross is always drawn: a
  face symbol is a fraction of a pixel until what places it scales it up, and
  then it is the only thing to see."
  []
  (let [{:keys [where point]} @(rf/subscribe [::sub/drop])
        {:keys [shapes center]} @drag/carrying]
    (when (and (= :stage where) point)
      (let [[x y] (drag/pos-for point)
            [cx cy] (or center [0 0])]
        [:g.ghost {:transform (str "translate(" x "," y ")")}
         (doall
          (map-indexed
           (fn [i {:keys [kind pts cx cy r size]}]
             (case kind
               :poly ^{:key i} [:polygon {:points (points-text pts)}]
               :disc ^{:key i} [:circle {:cx cx :cy cy :r r}]
               :rect ^{:key i} [:rect {:x (- cx (/ size 2)) :y (- cy (/ size 2))
                                       :width size :height size}]
               nil))
           shapes))
         [:path {:d (str "M " (- cx 5) " " cy " H " (+ cx 5)
                         " M " cx " " (- cy 5) " V " (+ cy 5))}]]))))

(defn- xy
  "Where a pointer event is, in stage pixels, unrounded and unclamped: a drag
  holding the pointer may leave the stage and still be moving something."
  [^js svg event w h]
  (event-point svg event))

;; The move, turn or scale being dragged, as `dragging` is for a vertex: the
;; placement and transform it started from, and where. What it makes of the
;; pointer goes out as `::ui/gesture`, and on the way up as one `::ui/transform`.
(defonce ^:private gesture (atom nil))
(defonce ^:private marquee (r/atom nil))

;; An ⌥-drag of a peg's cross: where the pivot is being put, in stage pixels.
;; A REAGENT atom and not the plain `gesture` one, because the only thing that
;; moves during it is the cross — a repivot holds everything else still by
;; construction — so this drives a re-render and the document is left alone until
;; the pointer goes up.
(defonce ^:private repivot (r/atom nil))

(defn- loaded
  "The document and its tier-2 store, read WHEN THE POINTER GOES DOWN.

  NOT AT RENDER TIME, which is the bug it does not look like. `footage/store` is
  a mutable handle behind an id, and — see `events/edit` — the id does not change
  when the document does; `:paint/revision` is what says it did, and nothing this
  component subscribes to reads it. So a clip dereferenced while rendering is the
  clip as it was BEFORE the last edit, and a drag begun from one starts by
  putting the node back where it was two edits ago: the node sits still under the
  press and then jumps on the first pointermove, which is the whole of the drag
  that follows being right and its first frame being wrong. Every id here is
  reactive except the document, and the document is the one read late."
  [{:keys [clip-id]}]
  (store/entry clip-id))

(defn- select!
  "Select the node at row path `path` of the open symbol — the selection a
  timeline row makes, so the row, the inspector and the stage all show it — or
  the open symbol itself when `path` is empty."
  [{:keys [open f] :as ctx} path]
  (let [{document :clip st :store} (loaded ctx)]
    (if-let [{:keys [sid id]} (when (seq path)
                                (nest/placement document st open path f))]
      (rf/dispatch [::ui/select [:node sid id path]])
      (rf/dispatch [::ui/select nil]))))

(defn- begin!
  "Start dragging `kind` of the node at `path` from stage point `p`.

  THE PIVOT IS READ ONCE, when the pointer goes down, and held for the drag.
  `gesture/pivot` answers with the node's own `[:xform :pivot]` where it has one,
  and with the middle of what it draws where nobody has pivoted it yet — and
  holding that for the drag is what keeps a turn steady: re-deriving per
  pointermove would chase the box the turn is itself moving.

  A DRAG ON A NODE WITH NO PIVOT OF ITS OWN WRITES ONE, as part of its first
  edit — `gesture/with-pivot`. From then on the node turns about a point of its
  own, so `turn` writes `rot` alone and a keyed turn holds its pivot between its
  keys as well as on them."
  [{:keys [open f] :as ctx} kind path p]
  (let [{document :clip st :store} (loaded ctx)]
    (when-let [{:keys [sid id frame] :as pl} (nest/placement document st open path f)]
      (let [n  (get-in document [:symbols sid :nodes id])
            v0 (gesture/values n frame st)
            c  (gesture/pivot v0 ((pick/bounds-of document st sid n) frame))]
        (reset! gesture {:kind kind :pl pl :path path :open open :v0 v0 :p0 p :n n
                         :c c :a (gesture/angle pl c p) :turned 0})))))

(defn- stage-bounds [{:keys [world bounds]}]
  (when (and world bounds)
    (let [[x0 y0 x1 y1] bounds
          ps (pairs (through world [x0 y0 x1 y0 x1 y1 x0 y1]))]
      [(apply min (map first ps)) (apply min (map second ps))
       (apply max (map first ps)) (apply max (map second ps))])))

(defn- begin-many! [ctx kind placements p]
  (let [{document :clip st :store} (loaded ctx)
        members (into [] (keep (fn [{:keys [path]}]
                                 (when-let [{:keys [sid id frame] :as pl}
                                            (nest/placement document st (:open ctx) path (:f ctx))]
                                   (let [n (get-in document [:symbols sid :nodes id])]
                                     {:pl pl :path path :node n
                                      :v0 (gesture/values n frame st)}))))
                      placements)
        why (some #(gesture/refusal (:node %) kind) members)
        boxes (keep stage-bounds placements)
        box   (when (seq boxes)
                (reduce (fn [[ax ay bx by] [cx cy dx dy]]
                          [(min ax cx) (min ay cy) (max bx dx) (max by dy)]) boxes))]
    (cond
      why (rf/dispatch [::ui/refuse (str "the selection cannot move together: " why)])
      (and (seq members) box)
      (reset! gesture {:kind kind :members members :p0 p :box box}))))

(defn- multi-values
  "What one member of a multi-selection makes of the drag. `[cx cy]` is the
  middle of the shared box, in stage pixels — every member scales about THAT, so
  the selection keeps its arrangement.

  The whole of what this used to open-code — take the member's anchor to stage,
  scale it about the box, and write the position difference — is `scale-by`,
  because that was this same pivot equation with the stored anchor as the pivot."
  [kind {:keys [pl v0]} [cx cy] p0 p]
  (case kind
    :move (gesture/move pl v0 p0 p)
    :scale
    (let [d0 (max 1e-6 (js/Math.hypot (- (first p0) cx) (- (second p0) cy)))
          k  (/ (js/Math.hypot (- (first p) cx) (- (second p) cy)) d0)]
      (when-let [inv (node/invert (:parent pl))]
        (gesture/scale-by v0 (through inv [cx cy]) k)))
    nil))

(defn- drag! [p ^js event]
  (let [{:keys [kind pl path open v0 p0 n a c turned values]} @gesture
        shift? (.-shiftKey event)
        moved? (or values (< 1 (js/Math.hypot (- (first p) (first p0)) (- (second p) (second p0)))))]
    (when moved?
      (if-let [members (:members @gesture)]
        (let [[x0 y0 x1 y1] (:box @gesture)
              center [(/ (+ x0 x1) 2) (/ (+ y0 y1) 2)]
              edits (into [] (keep (fn [{:keys [pl path] :as member}]
                             (when-let [vs (multi-values kind member center p0 p)]
                               (assoc (select-keys pl [:sid :id :frame])
                                      :path path :values vs)))) members)]
          (swap! gesture assoc :values edits)
          (rf/dispatch [::ui/gesture {:edits edits}]))
        (if-let [why (gesture/refusal n kind)]
        (do (reset! gesture nil) (rf/dispatch [::ui/refuse why]))
        (let [vs (case kind
                   :move  (gesture/move pl v0 p0 p)
                   :scale (gesture/scale pl v0 c p0 p shift?)
                   :turn  (let [b  (gesture/angle pl c p)
                                d  (- b a)
                                t  (+ turned (- d (* 2 js/Math.PI (js/Math.round (/ d (* 2 js/Math.PI))))))
                                q  (/ js/Math.PI 12)]
                            (swap! gesture assoc :a b :turned t)
                            ;; ⇧ turns in 15° steps, as everywhere.
                            (gesture/turn v0 c (if shift?
                                                 (- (* q (js/Math.round (/ (+ (:rot v0) t) q))) (:rot v0))
                                                 t))))]
          (when vs
            (swap! gesture assoc :values vs)
            (rf/dispatch [::ui/gesture
                          (assoc (select-keys pl [:sid :id :frame])
                                 :path path :open open :values vs)]))))))))

(defn- let-go! [commit?]
  (when-let [{:keys [pl path open values members]} @gesture]
    (reset! gesture nil)
    (when values
      (rf/dispatch (cond
                     (not commit?) [::ui/gesture nil]
                     members [::ui/transform-many values]
                     :else [::ui/transform (assoc (select-keys pl [:sid :id :frame])
                                                  :path path :open open :values values)])))))

(defn- handles
  "The selected node's box, drawn through its own transform so it turns with
  it: a square on each corner to scale by, a knob above to turn by, and a cross
  on the pivot. Dragging inside it moves it — that is the stage's own
  pointerdown, which keeps a selection it lands inside.

  THE CROSS IS ON THE PIVOT, which is `[:xform :pivot]` through the node's
  parent — the point `node/local!` composes the rotation and the scale about, and
  therefore the point a turn here will actually hold still, on this frame and
  every frame between two keys. For a node nobody has pivoted yet it is the
  middle of the selection box, from the same `bounds` the box is drawn from, and
  the first turn or scale writes that down; `gesture/pivot` is the one rule and
  `subs/ui` applies it, so the overlay cannot disagree with the gesture.

  THE CROSS IS A HANDLE, ON EVERY NODE. ⌃/⌘-dragging it puts the pivot somewhere
  else — `::ui/repivot`, which moves nothing — and dragging it plainly moves the
  node, as dragging inside the box does. This is After Effects' pan-behind and
  Harmony's pivot handle; it is how an arm comes to turn about its shoulder
  rather than its middle, and it used to exist on pegs alone.

  A PEG GETS A ROSETTE INSTEAD OF A BOX, and it has to get something: it draws
  nothing, so it has no bounds to hang handles on — and the stage's other way in,
  dragging what is under the pointer, is `pick/choose` on the DRAWN ops, which
  never returns a node that draws nothing. Without this a peg could be made and
  then only be typed at in the inspector. So the handles fall back to a
  fixed-size cluster about its pivot: a knob turns it, four corners scale it.
  FIXED SIZE, in stage pixels, because there is no drawing to be proportional to
  — the same reason the ghost's cross is always drawn at a fixed size."
  [ctx]
  (let [{:keys [world parent bounds] piv :pivot} @(rf/subscribe [::sub/selected-placement])
        [_ _ _ path] @(rf/subscribe [::sub/selection])
        [px py] (when (and parent piv) (through parent piv))
        grab (fn [kind]
               (fn [^js event]
                 (.stopPropagation event)
                 (.preventDefault event)
                 (let [svg (.-ownerSVGElement (.-currentTarget event))]
                   (.setPointerCapture svg (.-pointerId event))
                   (begin! ctx kind path (xy svg event (:w ctx) (:h ctx))))))]
    (when (and world px)
      [:g.handles
       ;; FIRST, SO THE CORNERS AND THE KNOB WIN THE HIT TEST. The disc is 5
       ;; stage pixels across and some of the shapes here are a few pixels
       ;; across, so on a small drawing it covers its own box: a scale corner
       ;; painted after it is still grabbable, one painted before it is not. A
       ;; peg's rosette sits its corners 7 px out, spanning 5.2 to 8.8 from the
       ;; middle, which is the other reason the disc stops short of 5.2.
       [:circle.pivot-grab
        {:cx px :cy py :r 5
         ;; ⌃/⌘ moves the PIVOT instead of the node, which is the pan-behind
         ;; split: a plain drag writes `pos` and takes the drawing — and, on a
         ;; peg, everything hanging off it — with it, and that is a translate,
         ;; not a new pivot.
         ;;
         ;; NOT ⌥, which this was and which does not survive the trip. Most
         ;; Linux window managers grab Alt-drag to move the window, so the page
         ;; never sees the pointer at all and the gesture is simply missing — on
         ;; the machine it is missing from, with no error. ⌥ is still accepted
         ;; for anyone whose desktop leaves it alone, and the command key is the
         ;; one the rest of this stage already reaches for.
         ;;
         ;; ⇧ is deliberately NOT it: it means CONSTRAIN everywhere else here —
         ;; uniform scale, 15° turn steps — and it is what a snap to the
         ;; drawing's corners would want when this drag grows one.
         :on-pointer-down
         (fn [^js event]
           (.stopPropagation event)
           (.preventDefault event)
           (let [svg (.-ownerSVGElement (.-currentTarget event))
                 p   (xy svg event (:w ctx) (:h ctx))]
             (.setPointerCapture svg (.-pointerId event))
             (if (or (.-ctrlKey event) (.-metaKey event) (.-altKey event))
               (reset! repivot {:path path :p p})
               (begin! ctx :move path p))))}]
       (if-let [[x0 y0 x1 y1] bounds]
         (let [corners (partition 2 (through world [x0 y0 x1 y0 x1 y1 x0 y1]))
               [cx cy tx ty] (through world [(/ (+ x0 x1) 2) (/ (+ y0 y1) 2) (/ (+ x0 x1) 2) y0])
               len     (max 1e-6 (js/Math.hypot (- tx cx) (- ty cy)))
               [kx ky] [(+ tx (* 8 (/ (- tx cx) len))) (+ ty (* 8 (/ (- ty cy) len)))]]
           [:<>
            [:polygon.box {:points (points-text (flatten corners))}]
            [:line.knob-arm {:x1 tx :y1 ty :x2 kx :y2 ky}]
            [:circle.knob {:cx kx :cy ky :r 2.2 :on-pointer-down (grab :turn)}]
            (doall
             (for [[i [x y]] (map-indexed vector corners)]
               ^{:key i}
               [:rect.corner {:x (- x 1.8) :y (- y 1.8) :width 3.6 :height 3.6
                              :on-pointer-down (grab :scale)}]))])
         ;; A peg. Same three gestures, hung on nothing.
         (let [r 7]
           [:<>
            [:line.knob-arm {:x1 px :y1 (- py r) :x2 px :y2 (- py r 8)}]
            [:circle.knob {:cx px :cy (- py r 8) :r 2.2 :on-pointer-down (grab :turn)}]
            (doall
             (for [[i [x y]] (map-indexed vector [[(- px r) (- py r)] [(+ px r) (- py r)]
                                                  [(+ px r) (+ py r)] [(- px r) (+ py r)]])]
               ^{:key i}
               [:rect.corner {:x (- x 1.8) :y (- y 1.8) :width 3.6 :height 3.6
                              :on-pointer-down (grab :scale)}]))]))
       ;; The cross itself, last and unclickable: it marks the pivot, and while a
       ;; ⌃/⌘-drag is placing one it follows the pointer alone — nothing else
       ;; moves during a repivot, by construction, so there is nothing else to
       ;; redraw until the pointer goes up.
       (let [[cx cy] (or (:p @repivot) [px py])]
         [:path.pivot {:class (when @repivot "moving")
                       :d (str "M " (- cx 4) " " cy " H " (+ cx 4)
                               " M " cx " " (- cy 4) " V " (+ cy 4))}])])))

(defn- group-handles [ctx placements]
  (let [boxes (keep stage-bounds placements)]
    (when (seq boxes)
      (let [[x0 y0 x1 y1] (reduce (fn [[ax ay bx by] [cx cy dx dy]]
                                     [(min ax cx) (min ay cy) (max bx dx) (max by dy)]) boxes)
            grab (fn [kind]
                   (fn [^js event]
                     (.stopPropagation event)
                     (.preventDefault event)
                     (let [svg (.-ownerSVGElement (.-currentTarget event))]
                       (.setPointerCapture svg (.-pointerId event))
                       (begin-many! ctx kind placements (xy svg event (:w ctx) (:h ctx))))))]
        [:g.handles.multi
         [:rect.box {:x x0 :y y0 :width (- x1 x0) :height (- y1 y0)}]
         (doall (for [[i [x y]] (map-indexed vector [[x0 y0] [x1 y0] [x1 y1] [x0 y1]])]
                  ^{:key i} [:rect.corner {:x (- x 1.8) :y (- y 1.8)
                                           :width 3.6 :height 3.6
                                           :on-pointer-down (grab :scale)}]))]))))

(defn- address-for [{:keys [open f] :as ctx} path]
  (let [{document :clip st :store} (loaded ctx)]
    (when-let [{:keys [sid id]} (and (seq path) (nest/placement document st open path f))]
      [:node sid id path])))

(defn- creation-box
  "The effective creation parent's outline, resolved from selection and playhead.

  NOT THE SELECTION'S BOX AND DELIBERATELY NOT LIKE IT. The selection gets
  handles, because handles are how you change it; the target gets an outline
  with nothing to grab, because the derived destination is not itself a handle.
  Same geometry — the node's bounds through its own transform, so it turns with
  what it is around — and a different statement.

  THE TAG IS IN SCREEN SPACE, THE BOX IS NOT. The box goes through the node's
  matrix and therefore rotates; a name rotated with it would be unreadable at
  exactly the angles somebody is most likely to be working at. So the tag is
  pinned to whichever drawn corner ended up furthest bottom-right and written
  horizontally, just outside it. Its font size is in user units because this SVG
  carries a `viewBox` scaled by `zoom`, which is also why the stroke widths
  nearby are fractions."
  []
  (let [{:keys [world bounds id node]}
        @(rf/subscribe [::sub/creation-placement])
        ;; `::render/clip` rather than `loaded`: this runs at render time, which
        ;; is the one thing that docstring says the non-reactive read is not for.
        document @(rf/subscribe [::render/clip])
        n node
        label (when id (clip/node-label document id n))]
    (when (and world bounds)
      (let [[x0 y0 x1 y1] bounds
            corners (pairs (through world [x0 y0 x1 y0 x1 y1 x0 y1]))
            ;; Furthest bottom-right of the four as drawn: the corner a reader
            ;; would call "the bottom right" whatever the node has been turned to.
            [tx ty] (apply max-key (fn [[x y]] (+ x y)) corners)]
        [:g.creation-target {:pointer-events "none"}
         [:polygon.creation-target-box {:points (points-text (flatten corners))}]
         (when label
           [:g {:transform (str "translate(" (+ tx 1.5) " " (+ ty 1.5) ")")}
            ;; The plate behind the text, sized off the string rather than
            ;; measured: `textLength` would need a layout pass, and a tag that
            ;; is a little wide is better than one that reflows the frame.
            [:rect.creation-target-tag-bg {:x 0 :y 0 :rx 0.8
                               :width (+ 2 (* 2.1 (count label))) :height 5}]
            [:text.creation-target-tag {:x 1 :y 3.8} label]])]))))

;; ---------------------------------------------------------------------------
;; the drawing tools

;; Where the pointer is over the stage, in stage pixels: the brush's footprint
;; and the pen's edge marker follow it. A ratom, read only by `cursor` and
;; `points`, so a move re-renders those and not the overlay.
(defonce ^:private pointer (r/atom nil))

;; The brush or eraser stroke under the pointer, as `dragging` is for a vertex.
(defonce ^:private painting (atom nil))

(def ^:private close-by
  "Stage pixels from the first point at which a click closes the polygon."
  3)

(defn- snapped
  "`p` from the draft's last point, at the nearest 45° when ⇧ is held."
  [draft [x y :as p] ^js event]
  (if (and (.-shiftKey event) (<= 2 (count draft)))
    (let [ax (nth draft (- (count draft) 2)) ay (peek draft)
          a  (* (/ js/Math.PI 4) (js/Math.round (/ (js/Math.atan2 (- y ay) (- x ax)) (/ js/Math.PI 4))))
          d  (js/Math.hypot (- x ax) (- y ay))]
      [(js/Math.round (+ ax (* d (js/Math.cos a)))) (js/Math.round (+ ay (* d (js/Math.sin a))))])
    p))

(defn- closes? [draft [x y]]
  (and (<= 6 (count draft))
       (<= (js/Math.hypot (- x (first draft)) (- y (second draft))) close-by)))

(defn- on-edge
  "`[i t]`: the edge of ring `pts` that stage point `p` is on, from point `i`
  a fraction `t` of the way to the next — or nil."
  [pts [x y]]
  (let [ps (pairs pts) n (count ps)
        ;; Not a bridge: a point there would open the hole. See `outline-path`.
        there (set (map (fn [i] [(ps i) (ps (mod (inc i) n))]) (range n)))]
    (some (fn [i]
            (let [[ax ay] (ps i) [bx by] (ps (mod (inc i) n))
                  dx (- bx ax) dy (- by ay)
                  l2 (+ (* dx dx) (* dy dy))
                  t  (if (zero? l2) 0 (/ (+ (* (- x ax) dx) (* (- y ay) dy)) l2))]
              (when (and (< 0.05 t 0.95) (not (contains? there [[bx by] [ax ay]]))
                         (<= (js/Math.hypot (- x (+ ax (* t dx))) (- y (+ ay (* t dy)))) 1.5))
                [i t])))
          (range n))))

(defn- stroke-begin!
  "Start painting with the brush, or erasing, at stage point `p`.

  The eraser cuts the shapes in the symbol holding what the stroke STARTS on,
  of the colour it starts on — Photoshop's background eraser, Flash's erase
  fills — or, with ⌥, every one of them. Starting on nothing with ⌥ cuts in
  the open symbol."
  [{:keys [open f] :as ctx} tool p size tone ^js event]
  (let [eraser? (= :eraser tool)
        all?    (.-altKey event)
        op      (when eraser? (player/hit-op p))
        path    (when op (let [n (:node op)] (if (vector? n) n [n])))
        within  (if path (pop path) [])
        {document :clip st :store} (loaded ctx)
        colour  #(when-let [{:keys [sid id frame]} (nest/placement document st open % f)]
                   (when (get-in document [:symbols sid :nodes id :paint?])
                     (channel/value-at (get-in document [:symbols sid :nodes id :channels [:style :color]])
                                       frame st)))
        start   (when path (colour path))
        paths   (when (and eraser? (or all? path))
                  (when-let [{:keys [sid]} (nest/inside document st open within f)]
                    (into #{}
                          (keep (fn [[id n]]
                                  (let [q (conj within id) c (when (= :poly (:kind n)) (colour q))]
                                    (when (and (some? c) (not (symbol/knockout? c)) (or all? (= c start)))
                                      q))))
                          (get-in document [:symbols sid :nodes]))))
        ;; Over what is on screen, which is everywhere the pointer can paint.
        m       (let [[x0 y0 x1 y1] (or (player/visible (:w ctx)) [0 0 (:w ctx) (:h ctx)])]
                  (outline/stamp! (assoc (outline/mask (- x1 x0) (- y1 y0)) :origin [x0 y0]) p p size))
        s       (cond
                  ;; The brush's target is the creation target, which the player
                  ;; reads for itself.
                  (not eraser?) {:mask m :slot tone :knock (when (= :clear tone) -1)}
                  (seq paths)   {:mask m :paths paths})]
    (if s
      (do (reset! painting (assoc s :last p :size size :version 0))
          (player/stroke! (assoc s :version 0)))
      (rf/dispatch [::ui/refuse "nothing to erase there — ⌥ erases every colour"]))))

(defn- stroke-move! [p]
  (when-let [{:keys [mask last size]} @painting]
    (outline/stamp! mask last p size)
    ;; The version says the mask changed under the same object, so the player
    ;; traces it again — once a frame at most, however fast the pointer moves.
    (player/stroke! (swap! painting #(-> % (assoc :last p) (update :version inc))))))

(defn- stroke-end! []
  (when-let [{:keys [mask paths]} @painting]
    (reset! painting nil)
    ;; Synchronously, so the shapes are in the document before the preview of
    ;; them goes: otherwise the stroke blinks out for a frame in between.
    (rf/dispatch-sync [::ui/stroke {:pieces (outline/pieces mask) :paths paths}])
    (player/stroke! nil)))

(defn- cursor
  "The brush's footprint under the pointer, the size it paints."
  [tool]
  (let [size @(rf/subscribe [::sub/brush])]
    (when-let [[x y] @pointer]
      [:circle {:class (str "footprint " (name tool)) :cx x :cy y :r (/ size 2)}])))

(defn- outline-path
  "Ring `pts` as an SVG path, without its bridges: an edge the ring runs along
  both ways is where a hole is joined to the outside (see `domain/outline`),
  which the fill never draws and the outline should not either."
  [pts]
  (let [ps (pairs pts) n (count ps)
        edges (map (fn [i] [(ps i) (ps (mod (inc i) n))]) (range n))
        there (set edges)]
    (apply str (for [[a b] edges :when (not (contains? there [b a]))]
                 (str "M" (first a) "," (second a) "L" (first b) "," (second b))))))

(defn- points
  "The pen on the selected shape: its points to drag, the one pressed last
  picked for Delete, ⌥-click to delete at once, and a marker where a click on
  an edge would add one."
  [ctx draft]
  (let [[sid id geom active editable? frame matrix] (editing)
        picked @(rf/subscribe [::sub/vertex])
        pts (when geom (through matrix (channel/value-at geom frame (:store (loaded ctx)))))
        edge (when (and editable? (empty? draft) (not (channel/nothing? pts)) @pointer)
               (on-edge pts @pointer))]
    (when (and pts (not (channel/nothing? pts)))
      [:g.points
       [:path.outline {:d (outline-path pts)}]
       (when-let [[i t] edge]
         (let [[ax ay] (nth (pairs pts) i) [bx by] (nth (pairs pts) (mod (inc i) (quot (count pts) 2)))]
           [:circle.insert {:cx (+ ax (* t (- bx ax))) :cy (+ ay (* t (- by ay))) :r 1.6}]))
       (when-let [inv (when editable? (node/invert matrix))]
         (doall
          (for [[i [x y]] (map-indexed vector (pairs pts))]
            ^{:key i}
            [:circle.vertex {:cx x :cy y :r 1.8
                             :class (when (= picked [sid id i]) "picked")
                             :on-pointer-down
                             (fn [^js event]
                               (.stopPropagation event)
                               (.preventDefault event)
                               (if (.-altKey event)
                                 (rf/dispatch [::ui/delete-vertex sid id i])
                                 (do (.setPointerCapture (.-currentTarget event) (.-pointerId event))
                                     (rf/dispatch [::ui/pick-vertex sid id i])
                                     (reset! dragging [sid id active i inv]))))}])))])))

(defn- draft-lines
  "The pen's draft as hairlines — the fill is in the picture — with the segment
  to the pointer, and a ring on the first point when a click there closes it."
  [draft hover]
  (when (seq draft)
    (let [[fx fy] draft]
      [:g.draft
       [:polyline {:points (points-text (cond-> draft hover (into hover)))}]
       [:circle {:class (str "first" (when (and hover (closes? draft hover)) " closing"))
                 :cx fx :cy fy :r 1.8}]])))

(defn- abandon!
  "Whatever a pointer was doing on the stage, dropped rather than finished: the
  browser took the pointer back, or a second finger came down to move the view."
  []
  (reset! dragging nil) (reset! marquee nil)
  (reset! painting nil) (player/stroke! nil)
  (let-go! false))

(defn- overlay [w h zoom [x0 y0 x1 y1]]
  (let [tool  @(rf/subscribe [::sub/tool])
        draft @(rf/subscribe [::sub/draft])
        hover @(rf/subscribe [::sub/hover])
        tone  @(rf/subscribe [::sub/tone])
        size  @(rf/subscribe [::sub/brush])
        [_ _ _ selected] @(rf/subscribe [::sub/selection])
        selections @(rf/subscribe [::sub/selections])
        placements @(rf/subscribe [::sub/selected-placements])
        selected-paths (set (keep #(nth % 3 nil) selections))
        clip-id @(rf/subscribe [::render/clip-id])
        ctx {:clip-id clip-id
             ;; The open symbol's OWN frame, which is what `nest/placement`
             ;; walks in: picking and dragging on the stage are edits, and an
             ;; edit's coordinate is the document's.
             :open @(rf/subscribe [::render/open]) :f @(rf/subscribe [::render/open-frame])
             :w w :h h}
        pen? (= :pen tool)
        ;; The shape the pen is on, read while rendering: a click handler is
        ;; not a reactive context to subscribe from.
        [esid eid egeom _ editable? eframe matrix] (editing)
        selected-pts (when (and egeom matrix)
                       (let [pts (channel/value-at egeom eframe (:store (loaded ctx)))]
                         (when-not (channel/nothing? pts)
                           (through matrix pts))))
        paints? (#{:brush :eraser} tool)
        mark @marquee]
    [:svg {:class (str "paint-overlay tool-" (name tool)
                       (when (and pen? hover (closes? draft hover)) " closing"))
           :width (* zoom (- x1 x0)) :height (* zoom (- y1 y0))
           :view-box (str x0 " " y0 " " (- x1 x0) " " (- y1 y0))
           :tab-index -1
           :on-pointer-down
           ;; ONE POINTER DRAWS: the first finger down. A second is the view's —
           ;; see `navigation` — and fed into a stroke it zigzags between the two.
           (fn [^js event]
             (when (.-isPrimary event)
             (let [svg (.-currentTarget event)
                   p   (xy svg event w h)]
               ;; Without scrolling it into view: the overlay is the whole work
               ;; area, and a phone scrolled to show its corner, which threw the
               ;; stage off screen on a tap and every point after it sideways.
               (.focus svg #js {:preventScroll true})
               (case tool
                 :pen
                 (let [q (snapped draft (stage-point event w h) event)
                       pts  (when (and egeom editable? (empty? draft))
                              (through matrix (channel/value-at egeom eframe (:store (loaded ctx)))))
                       edge (when (and pts (not (channel/nothing? pts))) (on-edge pts p))]
                   (cond
                     (closes? draft q) (rf/dispatch [::ui/finish-polygon])
                     edge (rf/dispatch [::ui/insert-vertex esid eid (first edge) (second edge)])
                     :else (rf/dispatch [::ui/add-draft-point (first q) (second q)])))

                 (:brush :eraser)
                 (do (.setPointerCapture svg (.-pointerId event))
                     (stroke-begin! ctx tool p size tone event))

                 ;; WITH THE SELECTION, so a shape the hit-test treats as light
                 ;; or as a hole — a palette swapper, a knockout — can be
                 ;; dragged by the one you are holding. See `pick/hit-op`.
                 (let [path (pick/choose selected (player/at p selected) (.-altKey event))]
                   (when (and path (not (.-shiftKey event)))
                     (reset! selection-anchor {:clip-id clip-id :open (:open ctx) :path path}))
                   (cond
                     (and (or (.-metaKey event) (.-ctrlKey event)) path)
                     (when-let [address (address-for ctx path)]
                       (rf/dispatch [::ui/toggle-selection address]))

                     (and (.-shiftKey event) path)
                     (when-let [address (address-for ctx path)]
                       (let [document (:clip (loaded ctx))
                             expanded @(rf/subscribe [::sub/expanded])
                             xs (vec (keep :select (timeline/rows document (:open ctx) expanded selected)))
                             origin (if (and (= clip-id (:clip-id @selection-anchor))
                                             (= (:open ctx) (:open @selection-anchor)))
                                      (:path @selection-anchor) selected)
                             anchor (when origin (address-for ctx origin))
                             a (.indexOf xs anchor) b (.indexOf xs address)]
                         (rf/dispatch (if (and (<= 0 a) (<= 0 b))
                                        [::ui/select-many (subvec xs (min a b) (inc (max a b)))]
                                        [::ui/select address]))))

                     path
                     (when-not (contains? selected-paths path) (select! ctx path))

                     :else
                     (do (.setPointerCapture svg (.-pointerId event))
                         (reset! marquee {:p0 p :p p :more? (or (.-metaKey event) (.-ctrlKey event) (.-shiftKey event))})))
                   (when (and path (not (or (.-shiftKey event) (.-metaKey event) (.-ctrlKey event))))
                     (.setPointerCapture svg (.-pointerId event))
                     (if (and (> (count placements) 1) (contains? selected-paths path))
                       (begin-many! ctx :move placements p)
                       (begin! ctx :move path p))))))))
           :on-double-click (fn [^js event]
                              (when (= :select tool)
                                (let [p (xy (.-currentTarget event) event w h)
                                      hit  (player/at p selected)
                                      path (pick/deeper selected hit)]
                                  (cond
                                    (not= path selected) (select! ctx path)
                                    ;; Into the shape: the pen, on its points.
                                    (= hit selected) (rf/dispatch [::ui/set-tool :pen])))))
           :on-key-down (fn [^js event]
                          ;; Out a level, as Figma's Esc: the instance that
                          ;; holds what is selected, then nothing.
                          (when (and (= "Escape" (.-key event)) selected (= :select tool))
                            (select! ctx (pop selected))))
           :on-pointer-leave (fn [_] (reset! pointer nil))
           :on-pointer-move
           (fn [^js event]
             (when (.-isPrimary event)
             (let [p (xy (.-currentTarget event) event w h)]
               (reset! pointer p)
               (cond
                 ;; Back through the inverse of what the handle was drawn
                 ;; through, into the shape's own coordinates.
                 @dragging (let [[sid node key-frame vertex inv] @dragging]
                             (rf/dispatch [::paint-events/set-vertex sid node key-frame vertex
                                           (through inv (stage-point event w h))]))
                 @painting (stroke-move! p)
                 (and pen? (seq draft)) (let [q (snapped draft (stage-point event w h) event)]
                                          (when (not= q hover) (rf/dispatch [::ui/hover q])))
                 @repivot (swap! repivot assoc :p p)
                 @marquee (swap! marquee assoc :p p)
                 @gesture (drag! p event)))))
           :on-pointer-up     (fn [^js event]
                                (when (.-isPrimary event)
                                (reset! dragging nil)
                                (stroke-end!)
                                ;; One edit, at the end: see `::ui/repivot`.
                                (when-let [{:keys [path p]} @repivot]
                                  (reset! repivot nil)
                                  (rf/dispatch [::ui/repivot path p]))
                                (if-let [{:keys [p0 p more?]} @marquee]
                                  (let [depth (or (some-> selected count) 1)
                                        paths (player/in-rect [(first p0) (second p0)
                                                               (first p) (second p)] depth)
                                        addresses (into [] (keep #(address-for ctx %)) paths)
                                        old (if more? selections [])]
                                    (reset! marquee nil)
                                    (rf/dispatch [::ui/select-many (into old addresses)]))
                                  (let-go! true))))
           :on-pointer-cancel (fn [_] (abandon!))}
     [ghost]
     (when (seq selected-pts)
       [:polygon.selected-paint-outline
        {:points (points-text selected-pts) :pointer-events "none"}])
     (when mark
       (let [[[x0 y0] [x1 y1]] [(:p0 mark) (:p mark)]]
         [:rect.marquee {:x (min x0 x1) :y (min y0 y1)
                         :width (js/Math.abs (- x1 x0))
                         :height (js/Math.abs (- y1 y0))}]))
     ;; DRAWN WHILE DRAWING, unlike the handles. "Where will this polygon land"
     ;; is the question the outline exists to answer, and the moment it is being
     ;; asked is mid-draft.
     [creation-box]
     (when (= :select tool)
       (if (> (count placements) 1) [group-handles ctx placements] [handles ctx]))
     (when pen? [points ctx draft])
     (when pen? [draft-lines draft hover])
     (when paints? [cursor tool])]))

(defonce pan (atom nil))
(defonce navigation-blur
  (when (exists? js/window)
    (.addEventListener js/window "blur" #(reset! pan nil))))

(defonce pinch-start-zoom (atom nil))

;; Two fingers on the stage: `{:zoom :spread :at}` as they came down, and where
;; their middle was last.
(defonce ^:private two (atom nil))

(defn- middle [^js touches]
  (let [a (aget touches 0) b (aget touches 1)]
    [(/ (+ (.-clientX a) (.-clientX b)) 2) (/ (+ (.-clientY a) (.-clientY b)) 2)]))

(defn- apart [^js touches]
  (let [a (aget touches 0) b (aget touches 1)]
    (js/Math.hypot (- (.-clientX a) (.-clientX b)) (- (.-clientY a) (.-clientY b)))))

(defn- navigation []
  {:title "Two-finger scroll to pan · Pinch or ⌘/Ctrl-wheel to zoom · Space-drag or middle-drag to pan"
   ;; The picture is what is on screen, so moving or resizing the view paints
   ;; it again.
   :on-scroll player/repaint!
   :ref (fn [el]
          (when el
            (.observe (js/ResizeObserver. player/repaint!) el)
            (.observe (js/ResizeObserver. #(layout/zoomed! el)) (.querySelector el ".stage-wrap"))
            (set! (.-onwheel el)
              (fn [e]
                (.preventDefault e)
                ;; Chromium and Firefox deliver trackpad pinch as Ctrl-wheel.
                ;; Ordinary scrolling pans without guessing the input device.
                (when-not @pinch-start-zoom
                  (let [unit (case (.-deltaMode e) 1 16 2 (.-clientHeight el) 1)
                        dx (* unit (.-deltaX e)) dy (* unit (.-deltaY e))]
                    (if (or (.-ctrlKey e) (.-metaKey e))
                      (layout/zoom-at! el (.-clientX e) (.-clientY e)
                                (* @(rf/subscribe [::layout/zoom :stage])
                                   (js/Math.exp (* (if (.-ctrlKey e) -0.01 -0.002) dy))))
                      (do
                        (set! (.-scrollLeft el) (+ (.-scrollLeft el) dx))
                        (set! (.-scrollTop el) (+ (.-scrollTop el) dy))))))))
            ;; ON A PHONE ONE FINGER IS THE PEN AND TWO ARE THE VIEW. Nothing on
            ;; the stage scrolls under one finger — `touch-action` says so, and
            ;; is not trusted on an SVG in iOS — and two pan by their middle and
            ;; zoom by their spread, with whatever the first had begun dropped.
            ;; Properties rather than listeners, as `onwheel` is: this ref runs on
            ;; every render. Not passive, being on an element, so they can stop
            ;; the page.
            (set! (.-ontouchstart el)
              (fn [^js e]
                (let [ts (.-touches e)]
                  (when (<= 2 (.-length ts))
                    (.preventDefault e)
                    (abandon!)
                    (reset! two {:zoom @(rf/subscribe [::layout/zoom :stage])
                                 :spread (apart ts) :at (middle ts)})))))
            (set! (.-ontouchmove el)
              (fn [^js e]
                (let [ts (.-touches e)]
                  (cond
                    (and @two (<= 2 (.-length ts)))
                    (let [{:keys [zoom at] s0 :spread} @two
                          [x y :as m] (middle ts)
                          z (* zoom (/ (apart ts) (max 1 s0)))]
                      (.preventDefault e)
                      (if (< (js/Math.abs (- 1 (/ z @(rf/subscribe [::layout/zoom :stage])))) 0.02)
                        (do (set! (.-scrollLeft el) (+ (.-scrollLeft el) (- (first at) x)))
                            (set! (.-scrollTop el) (+ (.-scrollTop el) (- (second at) y))))
                        (layout/zoom-at! el x y z))
                      (swap! two assoc :at m))

                    (.closest (.-target e) ".paint-overlay")
                    (.preventDefault e)))))
            (set! (.-ontouchend el)
              (fn [^js e] (when (< (.-length (.-touches e)) 2) (reset! two nil))))
            (set! (.-ontouchcancel el) (.-ontouchend el))
            ;; Safari exposes pinch as GestureEvents, with cumulative scale. On a
            ;; phone the touches above have it already.
            (set! (.-ongesturestart el)
              (fn [e]
                (.preventDefault e)
                (when-not @two
                  (reset! pinch-start-zoom @(rf/subscribe [::layout/zoom :stage])))))
            (set! (.-ongesturechange el)
              (fn [e]
                (.preventDefault e)
                (when-let [z @pinch-start-zoom]
                  (layout/zoom-at! el (.-clientX e) (.-clientY e) (* z (.-scale e))))))
            (set! (.-ongestureend el)
              (fn [e] (.preventDefault e) (reset! pinch-start-zoom nil))))
          nil)
   :on-pointer-down-capture
   (fn [e] (when (or (= 1 (.-button e)) (and (= 0 (.-button e)) @shortcuts/space))
             (.preventDefault e) (.stopPropagation e)
             ;; Space held and used to pan, so letting go does not also play.
             (when @shortcuts/space (reset! shortcuts/space :panned))
             (let [el (.-currentTarget e)]
               (reset! pan [(.-clientX e) (.-clientY e) (.-scrollLeft el) (.-scrollTop el)])
               (.setPointerCapture el (.-pointerId e)))))
   :on-pointer-move-capture
   (fn [e] (when-let [[x y sx sy] @pan]
             (.stopPropagation e)
             (set! (.-scrollLeft (.-currentTarget e)) (+ sx (- x (.-clientX e))))
             (set! (.-scrollTop (.-currentTarget e)) (+ sy (- y (.-clientY e))))))
   :on-pointer-up-capture (fn [e] (when @pan (.stopPropagation e) (reset! pan nil)))
   :on-pointer-cancel-capture (fn [_] (reset! pan nil))})

;; How the work area was last laid out, to keep what is on screen still when it
;; grows up or to the left — which moves the stage in it — and to centre a new
;; stage.
(defonce ^:private laid (atom nil))

(defn- relaid!
  "After the work area is laid out as `now`, from `was`."
  [was now]
  (let [^js area (.querySelector js/document ".stage-area")]
    (cond
      (not= (:stage was) (:stage now)) (layout/centre-stage!)
      (= (:zoom was) (:zoom now))
      (let [[ax ay] (:rect was) [bx by] (:rect now) z (:zoom now)]
        (set! (.-scrollLeft area) (+ (.-scrollLeft area) (* z (- ax bx))))
        (set! (.-scrollTop area) (+ (.-scrollTop area) (* z (- ay by))))))
    (player/repaint!)))

(defn view []
  ;; Reactive on the clip's dimensions, so selecting a clip of another size
  ;; lays the stage out again; the player sizes the picture for itself.
  (let [w @(rf/subscribe [::playback/width])
        h @(rf/subscribe [::playback/height])
        ;; A drop lands at the playhead IN THE OPEN SYMBOL's frames, the same
        ;; unit a drop on the timeline's ruler lands in.
        frame @(rf/subscribe [::render/open-frame])
        clip @(rf/subscribe [::render/clip])
        destination @(rf/subscribe [::sub/creation-target])
        destination-name (when-let [sid (:sid destination)]
                           (clip/symbol-name clip sid))
        zoom @(rf/subscribe [::layout/zoom :stage])
        {:keys [on? opacity]} @(rf/subscribe [::layout/mat])
        opacity (if on? opacity 0)
        ;; The work area round the stage: as far as anything is drawn, and a
        ;; stage further. Margins and not an element round it, so it scrolls to
        ;; the left and the top as well. See `layout/board`.
        {:keys [stage rect]} @layout/board
        [x0 y0 x1 y1 :as rect] (if (= stage [w h]) rect [(- w) (- h) (* 2 w) (* 2 h)])
        now {:stage [w h] :zoom zoom :rect rect}
        was @laid]
    (when (not= was now)
      (reset! laid now)
      (r/after-render #(relaid! was now)))
    [:div.stage-area
     (navigation)
     [:div.stage-wrap
      ;; A drop on the stage lands at the PLAYHEAD, where the pointer is in space;
      ;; a drop on the timeline lands where the pointer is in time.
      ;; `dragenter` is cancelled as well as `dragover`: a drop target has to
      ;; accept on BOTH, and the element under the pointer changes whenever the
      ;; preview re-renders beneath it, which fires a fresh `dragenter`.
      {:data-width w :data-height h
       :style {:width (* zoom w) :height (* zoom h)
               :margin (str (* zoom (- y0)) "px " (* zoom (- x1 w)) "px "
                            (* zoom (- y1 h)) "px " (* zoom (- x0)) "px")}
       :on-drag-enter (fn [^js event] (when (drag/accepts?) (.preventDefault event)))
       :on-drag-over (fn [^js event]
                       (when (drag/accepts?)
                         (.preventDefault event)
                         (drag/hover! :stage frame (stage-point event w h))))
       :on-drag-leave (fn [^js event]
                        (when-not (.contains (.-currentTarget event) (.-relatedTarget event))
                          (rf/dispatch [::ui/drop-clear])))
       :on-drop (fn [^js event]
                  (.preventDefault event)
                  (drag/land! frame (stage-point event w h)))}
      ;; Sized and placed by the player, to what is drawn and on screen.
      [:canvas.stage {:ref #(player/set-canvas! %)}]
      ;; These too: see `player/paint-onions!` and `tracing/paint!`.
      [:canvas.onion-skin {:ref #(player/set-onion-canvas! %)}]
      [:canvas.tracing {:ref #(tracing/set-canvas! %)}]
      [:div.stage-shade {:style {:box-shadow (str "0 0 0 " (* zoom (max (- x1 x0) (- y1 y0))) "px rgba(0,0,0," opacity ")")}}]
      [:div.stage-overlay-wrap {:style {:left (* zoom x0) :top (* zoom y0)}}
       [overlay w h zoom rect]]
      (when destination-name
        [:div.stage-target "creating in " [:strong destination-name]])]
     [tools/adjust-last]]))
